import { Button, Modal, SimpleGrid, Stack, Textarea } from "@mantine/core";
import { useState } from "react";
import type { FeedbackType } from "./MessageUtils";

const FEEDBACK_CATEGORIES = [
    "Out of date",
    "Inaccurate",
    "Wrong sources",
    "Too long",
    "Too short",
    "Other",
];

interface NegativeFeedbackModalProps {
    opened: boolean;
    onClose: () => void;
    placeholder?: string;
    feedbackType: FeedbackType;
    onSubmit: (feedbackType: FeedbackType, feedback: string) => Promise<void>;
}

export const NegativeFeedbackModal = ({
    opened,
    onClose,
    placeholder,
    feedbackType,
    onSubmit,
}: NegativeFeedbackModalProps) => {
    const [feedback, setFeedback] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        setFeedback("");
        setSelectedCategory(null);
        onClose();
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const formattedFeedback = selectedCategory
            ? feedback
                ? `${selectedCategory}:${feedback}` // Both category and feedback exist
                : selectedCategory // Only category selected
            : feedback; // Only feedback present (no category selected)
        await onSubmit(feedbackType, formattedFeedback);
        setFeedback("");
        setSelectedCategory(null);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Modal
            size="lg"
            opened={opened}
            onClose={handleClose}
            title="What didn't you like about this response?"
            data-testid="feedback-modal"
        >
            <Stack gap="md">
                <SimpleGrid cols={3} spacing="xs">
                    {FEEDBACK_CATEGORIES.map((category) => (
                        <Button
                            key={category}
                            variant={selectedCategory === category ? "filled" : "outline"}
                            size="sm"
                            onClick={() =>
                                setSelectedCategory(selectedCategory === category ? null : category)
                            }
                            data-testid={`feedback-category-${category.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {category}
                        </Button>
                    ))}
                </SimpleGrid>
                <Textarea
                    placeholder={placeholder}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    style={{ width: "100%" }}
                    minRows={4}
                    data-testid="feedback-textarea"
                />
                <Button
                    loading={isSubmitting}
                    onClick={handleSubmit}
                    data-testid="submit-feedback-button"
                    style={{ alignSelf: "flex-end" }}
                >
                    Submit
                </Button>
            </Stack>
        </Modal>
    );
};
